import {useMemo} from 'react'
import {useQuery} from '@tanstack/react-query'
import {UserPlus} from 'lucide-react'
import {useNavigate} from 'react-router-dom'
import {routes} from '@/lib/routes'
import {useServerPagination, SortDir} from '@/hooks/use-server-pagination'
import {IdentityControllerIdentitiesEndpoint} from '@/types/endpoint-contracts'
import {omitContractFilters} from '@/types/endpoint-contract'
import {useI18n} from '@/i18n'
import {useServices} from '@/services/context'
import {Icon} from '@/components/shared/Icon'
import {UnifiedSearchBar} from '@/components/shared/UnifiedSearchBar'
import {Button} from '@/components/ui/button'
import {PageLayout} from '@/components/shared/PageLayout'
import {TableEmptyState} from '@/components/shared/TableEmptyState'
import {Badge, BadgeVariant} from '@/components/ui/badge'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table'
import {CONCEPT_ICON, type ConceptKey} from '@/lib/concepts'
import {Pagination} from '@/components/ui/pagination'
import {IdentityLifecycleStateBadge} from '@/types/identity'
import {useAppConfig, labelOf} from '@/hooks/use-app-config'
import {useAuth} from '@/contexts/auth-context'
import {permissions} from '@/lib/permissions'
import {formatDateOnly} from '@/lib/date-utils'
import {ClickableTableRow} from '@/components/shared/ClickableTableRow'
import {MutedText} from '@/components/shared/MutedText'

/**
 * Identities list page — Phase C.1 unified search migration (issue 342.06).
 * Replaces the legacy SearchInput + inline identityType / lifecycleState
 * Selects with a single {@link UnifiedSearchBar} driven by the
 * {@link IdentityControllerIdentitiesEndpoint} contract.
 *
 * Spec invariants this component upholds:
 *   • Zero implicit default filters — an empty URL shows every identity.
 *   • One request per submit / chip removal — the text input only fires a
 *     network round-trip when the user presses Enter.
 *   • `q` matches the identity's @Searchable display name and the joined
 *     party.displayName via the 1-hop SearchableContractRegistry path.
 */
export interface PromotedListTheme {
    title: string
    icon?: ConceptKey
    createLabel?: string
    countLabel?: (params: {count: number}) => string
}

export interface IdentitiesProps {
    /** Pinned identityType — hides the type filter and re-themes the page chrome (issue 301). */
    lockedType?: string
    /** Optional re-themed page chrome for the promoted by-type route. */
    themedAs?: PromotedListTheme
}

const IDENTITY_TYPE_PARAM = 'identityType'

export function Identities({lockedType, themedAs}: IdentitiesProps = {}) {
    const services = useServices()
    const i18n = useI18n()
    const {identityTypes, lifecycleStates} = useAppConfig()
    const {hasPermission} = useAuth()
    const navigate = useNavigate()

    const effectiveContract = useMemo(
        () => lockedType
            ? omitContractFilters(IdentityControllerIdentitiesEndpoint, [IDENTITY_TYPE_PARAM])
            : IdentityControllerIdentitiesEndpoint,
        [lockedType],
    )

    const {
        page, pageSize, queryString,
        setPage, setPageSize, pageSizeOptions,
        searchAndFilters, setSearchAndFilters,
    } = useServerPagination({
        endpoint: IdentityControllerIdentitiesEndpoint,
        sortable: true,
        defaultSortBy: IdentityControllerIdentitiesEndpoint.sort.PARTY_DISPLAY_NAME,
        defaultSortDir: SortDir.ASC,
    })

    const requestParams = useMemo(() => {
        const params = Object.fromEntries(new URLSearchParams(queryString))
        if (lockedType) params[IDENTITY_TYPE_PARAM] = lockedType
        return params
    }, [queryString, lockedType])

    const {data: identitiesData} = useQuery({
        queryKey: ['identities', queryString, page, pageSize, lockedType],
        queryFn: () => services.identities.list(requestParams),
    })

    const identities = identitiesData?.content || []
    const totalItems = identitiesData?.totalElements || 0
    const totalPages = identitiesData?.totalPages || 0

    const hasFilters = useMemo(() => {
        return !!searchAndFilters.q
            || Object.keys(searchAndFilters.filters).length > 0
            || Object.keys(searchAndFilters.dynamicAttributes).length > 0
    }, [searchAndFilters])

    const headerIcon = themedAs?.icon ? CONCEPT_ICON[themedAs.icon] : CONCEPT_ICON.identity
    const headerTitle = themedAs?.title ?? i18n.identities.allIdentities
    const countLabel = themedAs?.countLabel
        ? themedAs.countLabel({count: totalItems})
        : i18n.identities.identityCount({count: totalItems})
    const createLabel = themedAs?.createLabel ?? i18n.onboarding.actions.newUser

    return (
        <PageLayout
            variant="list"
            icon={headerIcon}
            title={headerTitle}
            count={totalItems}
            description={`${countLabel}${hasFilters ? ` ${i18n.common.matchingFilters}` : ''}`}
            data-testid="identities-list"
            actions={hasPermission(permissions.onboarding.create) ? (
                <Button
                    size="sm"
                    onClick={() => navigate(routes.identityManagement.onboarding.create())}
                    data-testid="btn-new-user"
                >
                    <Icon icon={UserPlus} className="mr-1"/>
                    {createLabel}
                </Button>
            ) : undefined}
            filters={
                <UnifiedSearchBar
                    contract={effectiveContract}
                    value={searchAndFilters}
                    onChange={setSearchAndFilters}
                    attributeEntityType="identity"
                    placeholder={i18n.identities.searchPlaceholder}
                    lockedDiscriminatorValues={lockedType ? [lockedType] : undefined}
                />
            }
            pagination={totalItems > 0 ? (
                <Pagination
                    page={page}
                    pageSize={pageSize}
                    totalItems={totalItems}
                    totalPages={totalPages}
                    onPageChange={setPage}
                    onPageSizeChange={setPageSize}
                    pageSizeOptions={pageSizeOptions}
                />
            ) : undefined}
        >
            <Table>
                <TableHeader>
                    <TableRow>
                        <TableHead data-testid="col-party">{i18n.identities.party}</TableHead>
                        <TableHead data-testid="col-display-name">{i18n.identities.derivedDisplayName}</TableHead>
                        <TableHead data-testid="col-type">{i18n.common.type}</TableHead>
                        <TableHead data-testid="col-lifecycle-state">{i18n.identities.lifecycleState}</TableHead>
                        <TableHead data-testid="col-entry-date">{i18n.identities.entryDate}</TableHead>
                        <TableHead data-testid="col-exit-date">{i18n.identities.exitDate}</TableHead>
                        <TableHead data-testid="col-created-at">{i18n.common.createdAt}</TableHead>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {identities.map((identity) => (
                        <ClickableTableRow
                            key={identity.id}
                            to={routes.identityManagement.identities.detail(identity.id)}
                            data-testid="identity-row"
                        >
                            <TableCell className="font-medium text-primary">
                                {identity.party.displayName}
                            </TableCell>
                            <TableCell>
                                <MutedText>{identity.derivedDisplayName ?? identity.party.displayName}</MutedText>
                            </TableCell>
                            <TableCell>
                                <Badge variant="outline">
                                    {labelOf(identityTypes, identity.identityType)}
                                </Badge>
                            </TableCell>
                            <TableCell>
                                {(() => {
                                    const badge = IdentityLifecycleStateBadge.of(identity.lifecycleState)
                                    const StateIcon = badge?.icon
                                    return (
                                        <Badge variant={badge?.variant ?? BadgeVariant.OUTLINE} className="gap-1">
                                            {StateIcon && <StateIcon className="h-3 w-3" aria-hidden="true"/>}
                                            {labelOf(lifecycleStates, identity.lifecycleState)}
                                        </Badge>
                                    )
                                })()}
                            </TableCell>
                            <TableCell>
                                <MutedText>{identity.entryDate ? formatDateOnly(identity.entryDate) : '\u2014'}</MutedText>
                            </TableCell>
                            <TableCell>
                                <MutedText>{identity.exitDate ? formatDateOnly(identity.exitDate) : '\u2014'}</MutedText>
                            </TableCell>
                            <TableCell>
                                <MutedText>{formatDateOnly(identity.createdAt)}</MutedText>
                            </TableCell>
                        </ClickableTableRow>
                    ))}
                    {identities.length === 0 && (
                        <TableEmptyState
                            colSpan={7}
                            hasFilters={hasFilters}
                            matchMessage={i18n.identities.noIdentitiesMatch}
                            emptyMessage={i18n.identities.noIdentitiesFound}
                        />
                    )}
                </TableBody>
            </Table>
        </PageLayout>
    )
}
